import { ref } from 'vue';
import { DEFAULT_UI_THEME, normalizeUiTheme, type UiThemeId } from './themeConstants';
import { updateUserTheme } from '@/helpers/api/users/users';

const theme = ref<UiThemeId>(DEFAULT_UI_THEME);

function syncBuiThemeAttribute(themeId: UiThemeId) {
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('data-bui-theme', themeId);
  }
}

/** Best locally known theme before auth (user blob, then legacy theme key). */
export function readBestKnownLocalTheme(): UiThemeId {
  const userBlob = localStorage.getItem('user');
  if (userBlob) {
    try {
      const parsed = JSON.parse(userBlob);
      if (parsed.uiTheme != null && parsed.uiTheme !== '') {
        return normalizeUiTheme(parsed.uiTheme);
      }
    } catch {
      // ignore malformed session blob
    }
  }
  const stored = localStorage.getItem('theme');
  return stored ? normalizeUiTheme(stored) : DEFAULT_UI_THEME;
}

/** Call before Vue mount to reduce first-paint flicker. */
export function bootstrapThemeFromLocalStorage() {
  applyThemeLocal(readBestKnownLocalTheme());
}

theme.value = readBestKnownLocalTheme();
syncBuiThemeAttribute(theme.value);

export function applyThemeLocal(themeName: UiThemeId) {
  theme.value = themeName;
  localStorage.setItem('theme', themeName);
  syncBuiThemeAttribute(themeName);
}

export interface SetThemeOptions {
  persistLocal?: boolean;
  syncServer?: boolean;
}

export function hydrateFromServer(uiTheme: string | null | undefined) {
  applyThemeLocal(normalizeUiTheme(uiTheme ?? undefined));
}

export function useTheme() {
  async function setTheme(
    themeName: string,
    { persistLocal = true, syncServer = true }: SetThemeOptions = {},
  ) {
    const normalized = normalizeUiTheme(themeName);
    const previous = theme.value;

    if (persistLocal) {
      applyThemeLocal(normalized);
    } else {
      theme.value = normalized;
      syncBuiThemeAttribute(normalized);
    }

    const { useUserStore } = await import('@/stores/user');
    const userStore = useUserStore();
    userStore.setUiTheme(normalized);

    if (!syncServer || !userStore.isLoggedIn || !userStore.userId) {
      return;
    }

    try {
      await updateUserTheme(userStore.userId, normalized);
    } catch (err) {
      applyThemeLocal(previous);
      userStore.setUiTheme(previous);
      throw err;
    }
  }

  return {
    theme,
    setTheme,
    hydrateFromServer,
  };
}

export default useTheme;
